import {v4 as uuidv4} from 'uuid';

import type {RuntimeEvent} from '../runtime/events';
import type {
  ActiveThreadLoadFailure,
  ContentPart,
  MessageNode,
  Thread,
  ThreadSummary,
} from './thread';

/** AgentChatAction is a discriminated union of all agent chat actions. */
export type AgentChatAction =
  | {type: 'createThread'; id: string; timestamp: number}
  | {type: 'deleteThread'; threadId: string; timestamp: number}
  | {type: 'setActiveThread'; threadId: string; timestamp: number}
  | {
      type: 'renameThread';
      threadId: string;
      title: string | null;
      timestamp: number;
    }
  | {type: 'loadThread'; thread: Thread; timestamp: number}
  | {type: 'beginLoadThread'; threadId: string; timestamp: number}
  | {
      type: 'failLoadThread';
      threadId: string;
      failure: ActiveThreadLoadFailure;
      timestamp: number;
    }
  | {type: 'clearThreadLoad'; threadId: string}
  | {
      type: 'addUserMessage';
      threadId: string;
      parentId: string | null;
      message: MessageNode;
      timestamp: number;
    }
  | {
      type: 'switchBranch';
      threadId: string;
      targetNodeId: string;
      timestamp: number;
    }
  | {
      type: 'applyRuntimeEvent';
      runId: string;
      threadId: string;
      event: RuntimeEvent;
      timestamp: number;
    }
  | {
      type: 'setThreadStatus';
      threadId: string;
      status: Thread['status'];
      timestamp: number;
    }
  | {
      type: 'setThreadError';
      threadId: string;
      error: string | null;
      timestamp: number;
    }
  | {
      type: 'setCancelRequested';
      threadId: string;
      value: boolean;
      timestamp: number;
    }
  | {
      type: 'resolveRunDisplayNames';
      threadId: string;
      results: Record<string, RunDisplayNameResolution>;
    }
  | {
      type: 'mergeThreadSummaries';
      summaries: readonly ThreadSummary[];
      timestamp: number;
    }
  | {type: 'beginRename'; threadId: string; title: string}
  | {type: 'endRename'; threadId: string}
  | {type: 'beginDelete'; threadId: string; timestamp: number}
  | {type: 'endDelete'; threadId: string}
  | {type: 'promoteDraft'; oldKey: string; threadId: string};

export type RunDisplayNameResolution =
  | {status: 'resolved'; displayName: string}
  | {status: 'not-found'}
  | {status: 'error'};

export function resolveRunDisplayNames(
  threadId: string,
  results: Record<string, RunDisplayNameResolution>
): AgentChatAction {
  return {type: 'resolveRunDisplayNames', threadId, results};
}

export function addUserMessage(
  threadId: string,
  parentId: string | null,
  content: ContentPart[],
  timestamp = Date.now()
): AgentChatAction {
  const message: MessageNode = {
    id: uuidv4(),
    role: 'user',
    content,
    createdAt: timestamp,
  };

  return {type: 'addUserMessage', threadId, parentId, message, timestamp};
}

export function createThread(
  id = uuidv4(),
  timestamp = Date.now()
): Extract<AgentChatAction, {type: 'createThread'}> {
  return {type: 'createThread', id, timestamp};
}

export function loadThread(
  thread: Thread,
  timestamp = Date.now()
): AgentChatAction {
  return {type: 'loadThread', thread, timestamp};
}

export function setActiveThread(
  threadId: string,
  timestamp = Date.now()
): AgentChatAction {
  return {type: 'setActiveThread', threadId, timestamp};
}

export function setThreadError(
  threadId: string,
  error: string | null,
  timestamp = Date.now()
): AgentChatAction {
  return {type: 'setThreadError', threadId, error, timestamp};
}

export function setThreadStatus(
  threadId: string,
  status: Thread['status'],
  timestamp = Date.now()
): AgentChatAction {
  return {type: 'setThreadStatus', threadId, status, timestamp};
}

export function setCancelRequested(
  threadId: string,
  value: boolean,
  timestamp = Date.now()
): AgentChatAction {
  return {type: 'setCancelRequested', threadId, value, timestamp};
}

export function beginLoadThread(
  threadId: string,
  timestamp = Date.now()
): AgentChatAction {
  return {type: 'beginLoadThread', threadId, timestamp};
}

export function failLoadThread(
  threadId: string,
  failure: ActiveThreadLoadFailure,
  timestamp = Date.now()
): AgentChatAction {
  return {type: 'failLoadThread', threadId, failure, timestamp};
}

/**
 * clearThreadLoad drops a thread's transient load state without loading or
 * failing it. Used to abandon a backgrounded load whose result is discarded.
 */
export function clearThreadLoad(threadId: string): AgentChatAction {
  return {type: 'clearThreadLoad', threadId};
}

export function renameThread(
  threadId: string,
  title: string | null,
  timestamp = Date.now()
): AgentChatAction {
  return {type: 'renameThread', threadId, title, timestamp};
}

export function mergeThreadSummaries(
  summaries: readonly ThreadSummary[],
  timestamp = Date.now()
): AgentChatAction {
  return {type: 'mergeThreadSummaries', summaries, timestamp};
}

export function beginRename(threadId: string, title: string): AgentChatAction {
  return {type: 'beginRename', threadId, title};
}

export function endRename(threadId: string): AgentChatAction {
  return {type: 'endRename', threadId};
}

export function deleteThread(
  threadId: string,
  timestamp = Date.now()
): AgentChatAction {
  return {type: 'deleteThread', threadId, timestamp};
}

export function beginDelete(
  threadId: string,
  timestamp = Date.now()
): AgentChatAction {
  return {type: 'beginDelete', threadId, timestamp};
}

export function endDelete(threadId: string): AgentChatAction {
  return {type: 'endDelete', threadId};
}

export function promoteDraft(
  oldKey: string,
  threadId: string
): AgentChatAction {
  return {type: 'promoteDraft', oldKey, threadId};
}
